import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import { Loading } from '@/components/ui/daisy';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useOptimisticReorder from '@/data/hook/useOptimisticReorder';
import useLessons from '@/data/hook/lesson/useLessons';
import { ROUTES, withTab } from '@/utils/routes';

import LessonCard from './LessonCard';
import { mountKey } from '@/utils/courses';
import LessonFormList from './LessonFormList';
import { LessonTabs } from '@/pages/course-line/LessonWorkspace';

type LessonsTabContentProps = {
  slug: string;
  unitId: number;
  courseLineId: number;
  moduleId: number;
};

export default function LessonsTabContent({
  slug,
  unitId,
  courseLineId,
  moduleId,
}: LessonsTabContentProps) {
  const {
    lessons,
    isLoading,
    createLesson,
    isCreating,
    deleteLesson,
    reorderLessons,
    isReordering,
  } = useLessons({ slug, unitId });

  const { optimistic: optimisticLessons, handleDragEnd } = useOptimisticReorder(
    {
      items: lessons,
      isReordering,
      onReorder: reorderLessons,
    },
  );

  const { navigate } = useCourseTreeNavigate();

  const handleEdit = (lessonId: number) => {
    navigate(
      ROUTES.ADMIN.COURSE_LINES.LESSON(
        courseLineId,
        moduleId,
        slug,
        unitId,
        lessonId,
      ),
      { treeKey: mountKey({ type: 'lesson', id: lessonId }) },
    );
  };

  const handleAddQuestions = (lessonId: number) => {
    navigate(
      withTab(
        ROUTES.ADMIN.COURSE_LINES.LESSON(
          courseLineId,
          moduleId,
          slug,
          unitId,
          lessonId,
        ),
        LessonTabs.QUESTIONS,
      ),
      {
        treeKey: mountKey({ type: 'lesson', id: lessonId }),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="relative">
      <DragDropList
        groupId={`lessons-list-${unitId}`}
        onDragEnd={handleDragEnd}
        disabled={isReordering}
        className="gap-0"
      >
        {optimisticLessons.map((lesson, index) => (
          <LessonCard
            key={lesson.id}
            lesson={lesson}
            showAddQuestions
            onAddQuestions={handleAddQuestions}
            onEdit={handleEdit}
            onDelete={deleteLesson}
            isDraggable
            id={String(lesson.id)}
            index={index}
            className="mb-6"
          />
        ))}
      </DragDropList>
      <LessonFormList
        lessonsCount={lessons.length}
        onSubmit={createLesson}
        isLoading={isCreating}
      />
    </div>
  );
}
